import type { OurContext } from '../context';
import { Routes } from '../../Routes';
import { errorReasons } from './mixpanelEvents';
import type { Status } from '../api/status';
import { useMemo, useState } from 'react';
import { useInterval } from '../util/util';
import { redirect } from '../api/redirect';
import { useApi } from '../../platform/api/useApi';
import { useNavigation } from '../../platform/navigation/useNavigation';
import { type NavigationAdapter } from '../../platform/navigation/NavigationAdapter';
import { type IframeAdapter } from '../../platform/iframe/IframeAdapter';
import { type DeviceAdapter } from '../../platform/device/DeviceAdapter';

export const handleStatus = (
  context: OurContext,
  status: Status | undefined,
  navigation: NavigationAdapter,
  iframe: IframeAdapter,
  device: DeviceAdapter,
  isWidgetSdkEmbed = false
) => {
  if (context.claims && isCompletedPaymentStatus(status)) {
    if (isWidgetSdkEmbed) {
      if (status === 'ACCEPTED') {
        context.setRoute(Routes.SUCCESS);
      } else if (status === 'REJECTED') {
        context.setRoute(Routes.CANCELLED);
      } else {
        context.setRoute(Routes.ERROR, errorReasons.PAYMENT_FAILED);
      }
    }
    redirect(
      context.claims.fallback,
      status,
      context.claims.brand,
      context.claims.apiSource,
      device.isMobile,
      navigation,
      iframe,
      !isWidgetSdkEmbed
    );
  } else if (status === 'TIMEOUT') {
    context.setRoute(Routes.TIMEOUT);
  } else if (status === 'ERROR') {
    context.setRoute(Routes.ERROR, errorReasons.STATUS_ERROR);
  } else if (status === 'SUBMITTED') {
    context.setRoute(Routes.STATUS);
  }
};

export function isCompletedPaymentStatus(
  status: Status | undefined
): status is 'ACCEPTED' | 'REJECTED' | 'FAILED' {
  return status === 'ACCEPTED' || status === 'REJECTED' || status === 'FAILED';
}

export function usePollStatus(
  context: OurContext,
  backoff = 3000,
  isWidgetSdkEmbed = false
): Status {
  const apis = useApi();
  const navigation = useNavigation();
  const [status, setStatus] = useState<Status>('PENDING');
  const pollingFinished = isWidgetSdkEmbed && isCompletedPaymentStatus(status);

  const statusCallback = useMemo(
    () => async () => {
      const historyState = navigation.getHistoryState() as { page?: string } | null;
      const isOnAbort = historyState?.page === 'abort';
      if (
        !context.claims ||
        ([Routes.ERROR, Routes.TIMEOUT] as Routes[]).includes(context.currentRoute) ||
        isOnAbort ||
        pollingFinished
      ) {
        return;
      }
      try {
        const newStatus = await apis.pollStatus(context);
        setStatus((previous) => {
          // Keep the first completed outcome when polling requests overlap.
          if (isWidgetSdkEmbed && isCompletedPaymentStatus(previous)) return previous;
          return newStatus;
        });
      } catch {
        // ignore and retry
      }
    },
    [context, apis, navigation, pollingFinished, isWidgetSdkEmbed]
  );

  useInterval(statusCallback, backoff);

  return status;
}
